import { useState, useEffect } from 'react';
import { Table, Input, Row, Col, Spinner, Badge } from 'reactstrap';
import { RefreshCw, AlertCircle, CheckCircle, Clock } from 'react-feather';
import { toast } from 'react-toastify';
import { Btn } from '../../../AbstractElements';
import AccessService from '../../../Api/services/access.service';
import type { components } from '../../../Api/types/openapi';
import { useTranslation } from 'react-i18next';

type AccessResponseWithUser = components['schemas']['AccessResponseWithUser'];
type PaginationAccessResponseWithUser = components['schemas']['Pagination_AccessResponseWithUser_'];

const AccessHistoryList = () => {
  const { t } = useTranslation();
  const [accessHistory, setAccessHistory] = useState<AccessResponseWithUser[]>([]);
  const [pagination, setPagination] = useState<PaginationAccessResponseWithUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);

  useEffect(() => {
    loadAccessHistory();
  }, [currentPage, searchTerm]);

  const loadAccessHistory = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: currentPage,
        limit,
      };

      if (searchTerm) {
        params.username = searchTerm;
      }

      const data = await AccessService.getAccessHistoryPaginated(params);
      
      if (!data || !Array.isArray(data.results)) {
        console.warn('Respuesta inválida del servidor: results no es un array');
        setAccessHistory([]);
        setPagination(null);
        return;
      }
      
      setAccessHistory(data.results);
      setPagination(data);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || t('Error al cargar historial de acceso');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }).format(date);
    } catch (error) {
      return dateString;
    }
  };

  return (
    <div>
      <Row className="mb-3">
        <Col md="6">
          <Input
            type="text"
            placeholder={t('Buscar por usuario...')}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </Col>
        <Col md="6" className="text-end">
          <Btn color="secondary" onClick={loadAccessHistory} className="me-2">
            <RefreshCw size={16} className="me-1" />
            {t('Actualizar')}
          </Btn>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5">
          <Spinner color="primary" />
        </div>
      ) : (
        <>
          <Table responsive striped>
            <thead>
              <tr>
                <th>{t('ID')}</th>
                <th>{t('Usuario')}</th>
                <th>{t('Nombre')}</th>
                <th>{t('IP')}</th>
                <th>{t('Fecha de Acceso')}</th>
                <th>{t('Estado')}</th>
              </tr>
            </thead>
            <tbody>
              {accessHistory.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center">
                    {t('No se encontraron registros de acceso')}
                  </td>
                </tr>
              ) : (
                accessHistory.map((access) => (
                  <tr key={access.id}>
                    <td>{access.id}</td>
                    <td>
                      <code>{access.username}</code>
                    </td>
                    <td>
                      {access.user?.name || '-'}
                    </td>
                    <td>
                      {access.ip ? <code>{access.ip}</code> : '-'}
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <Clock size={14} className="me-1" />
                        {formatDate(access.date_access)}
                      </div>
                    </td>
                    <td>
                      {access.failed ? (
                        <Badge color="danger" className="d-flex align-items-center justify-content-center" style={{ width: 'fit-content' }}>
                          <AlertCircle size={14} className="me-1" />
                          {t('Fallido')}
                        </Badge>
                      ) : (
                        <Badge color="success" className="d-flex align-items-center justify-content-center" style={{ width: 'fit-content' }}>
                          <CheckCircle size={14} className="me-1" />
                          {t('Exitoso')}
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          {pagination && pagination.pages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div>
                {t('Página')} {pagination.current_page} {t('de')} {pagination.pages}
              </div>
              <div>
                <Btn
                  color="primary"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                  className="me-2"
                >
                  {t('Anterior')}
                </Btn>
                <Btn
                  color="primary"
                  size="sm"
                  disabled={currentPage === pagination.pages}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  {t('Siguiente')}
                </Btn>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AccessHistoryList;

